/**
 * O que os plugins web acrescentam em Configurações da mesa → Plugins (docs/spec/web-plugins.md):
 * - Integrações (só mestres): o endereço de cada webhook que o plugin declara e quem pode disparar,
 *   com os últimos envios;
 * - Desenvolvimento: cada ficha ou tabela pode vir de um endereço do computador de quem programa,
 *   só neste navegador;
 * - o botão "Copiar instruções para IA" (o guia que acompanha o rpgdesk.js).
 */
import { useEffect, useState } from 'react';
import { useLanguage, useT, type Language } from '../../i18n/index.js';
import type { RoomPlugin } from '../../shared.js';
import { useApp } from '../../store.js';
import { Button } from '../../ui/controls.js';
import { toast } from '../../ui/Toaster.js';
import { serverUrl } from '../../serverUrl.js';
import aiGuidePt from '../../../../../packages/plugin-sdk/GUIA-IA.md?raw';
import aiGuideEn from '../../../../../packages/plugin-sdk/AI-GUIDE.md?raw';
import { devUrlOf, devUrlProblem, useDevUrls } from './devMode.js';
import { langOf, webhookUrlProblem } from './sdk.js';

const SDK_CDN = 'https://cdn.jsdelivr.net/npm/@rpgdesk/sdk@1/';

/**
 * O guia com o endereço do rpgdesk.js deste servidor: enquanto o @rpgdesk/sdk não está no npm, o do
 * jsDelivr não abre fora do RPG Desk, e o modo de teste (o arquivo aberto no computador) precisa dele.
 * Instalado, o servidor troca o endereço pela cópia dele de qualquer jeito.
 */
export function aiGuideText(language: Language = useLanguage.getState().language): string {
  const sdk = new URL(serverUrl('/p/_sdk/1/'), window.location.href).href;
  // o guia na língua da interface: a IA responde e escreve a ficha na mesma língua
  return (language === 'en' ? aiGuideEn : aiGuidePt).split(SDK_CDN).join(sdk);
}

export function AiGuideButton() {
  const t = useT();
  const copy = () => void navigator.clipboard.writeText(aiGuideText()).then(() => toast(t('webPlugin.aiGuide.copied'), 'success'));
  return (
    <Button variant="secondary" onClick={copy} title={t('webPlugin.aiGuide.hint')}>
      {t('webPlugin.aiGuide')}
    </Button>
  );
}

function Integrations({ plugin }: { plugin: RoomPlugin }) {
  const t = useT();
  const data = useApp((s) => s.pluginWebhooks[plugin.moduleId]);
  const loadWebhooks = useApp((s) => s.loadWebhooks);
  const setWebhook = useApp((s) => s.setWebhook);
  const [drafts, setDrafts] = useState<Record<string, { url: string; who: 'gm' | 'everyone' }>>({});
  useEffect(() => loadWebhooks(plugin.moduleId), [loadWebhooks, plugin.moduleId]);
  // o que chegou do servidor preenche só os campos que a pessoa ainda não mexeu
  useEffect(() => {
    if (!data) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const h of plugin.web?.webhooks ?? []) {
        if (next[h.name]) continue;
        const saved = data.hooks.find((s) => s.name === h.name);
        next[h.name] = { url: saved?.url ?? '', who: saved?.who ?? 'gm' };
      }
      return next;
    });
  }, [data, plugin.web?.webhooks]);
  const save = (name: string, url: string, who: 'gm' | 'everyone') => {
    const clean = url.trim();
    const problem = clean ? webhookUrlProblem(clean, langOf(useLanguage.getState().language)) : null;
    if (problem) return toast(problem, 'error');
    setWebhook(plugin.moduleId, name, clean || null, who);
  };
  return (
    <div className="rd-webset__block">
      <p className="rd-config__hint">{t('webPlugin.hooks.hint')}</p>
      <ul className="rd-webset__hooks">
        {(plugin.web?.webhooks ?? []).map((h) => {
          const draft = drafts[h.name] ?? { url: '', who: 'gm' as const };
          const saved = data?.hooks.find((s) => s.name === h.name);
          const update = (patch: Partial<typeof draft>) => setDrafts((all) => ({ ...all, [h.name]: { ...draft, ...patch } }));
          return (
            <li key={h.name}>
              <div>
                <code>{h.name}</code> <span className="rd-webset__muted">{h.description}</span>
              </div>
              <div className="rd-webset__row">
                <input
                  className="rd-webset__url"
                  value={draft.url}
                  placeholder="https://hooks.zapier.com/hooks/catch/…"
                  aria-label={t('webPlugin.hooks.url', { name: h.name })}
                  onChange={(e) => update({ url: e.target.value })}
                />
                <select aria-label={t('webPlugin.hooks.who', { name: h.name })} value={draft.who} onChange={(e) => update({ who: e.target.value === 'everyone' ? 'everyone' : 'gm' })}>
                  <option value="gm">{t('webPlugin.hooks.gm')}</option>
                  <option value="everyone">{t('webPlugin.hooks.everyone')}</option>
                </select>
                <Button variant="secondary" onClick={() => save(h.name, draft.url, draft.who)}>
                  {t('webPlugin.hooks.save')}
                </Button>
                {saved ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDrafts((all) => ({ ...all, [h.name]: { url: '', who: 'gm' } }));
                      setWebhook(plugin.moduleId, h.name, null, 'gm');
                    }}
                  >
                    {t('webPlugin.hooks.remove')}
                  </Button>
                ) : null}
              </div>
              <div className="rd-webset__muted">{saved ? t('webPlugin.hooks.on') : t('webPlugin.hooks.off')}</div>
            </li>
          );
        })}
      </ul>
      {data?.log.length ? (
        <>
          <p className="rd-webset__sub">{t('webPlugin.hooks.log')}</p>
          <ul className="rd-webset__log">
            {data.log.map((e, i) => (
              <li key={`${e.at}-${i}`}>
                {new Date(e.at).toLocaleTimeString()} · {e.nick} · <code>{e.name}</code> · {e.ok ? `✓ ${e.status}` : `✗ ${e.error ?? e.status}`}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function DevMode({ plugin }: { plugin: RoomPlugin }) {
  const t = useT();
  const urls = useDevUrls((s) => s.urls);
  const setUrl = useDevUrls((s) => s.set);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  return (
    <div className="rd-webset__block">
      <p className="rd-config__hint">{t('webPlugin.devmode.hint')}</p>
      <ul className="rd-webset__hooks">
        {plugin.dataTypes.map((d) => {
          const current = devUrlOf(urls, plugin.moduleId, d.id);
          const draft = drafts[d.id] ?? current ?? '';
          const save = () => {
            const url = draft.trim();
            const problem = url ? devUrlProblem(url, langOf(useLanguage.getState().language)) : null;
            if (problem) return toast(problem, 'error');
            setUrl(plugin.moduleId, d.id, url || null);
          };
          return (
            <li key={d.id}>
              <div>{d.title || d.id}</div>
              <div className="rd-webset__row">
                <input className="rd-webset__url" value={draft} placeholder="http://localhost:5173/ficha.html" aria-label={t('webPlugin.devmode.url', { title: d.title || d.id })} onChange={(e) => setDrafts((all) => ({ ...all, [d.id]: e.target.value }))} />
                <Button variant="secondary" onClick={save}>
                  {t('webPlugin.hooks.save')}
                </Button>
                {current ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setUrl(plugin.moduleId, d.id, null);
                      setDrafts((all) => ({ ...all, [d.id]: '' }));
                    }}
                  >
                    {t('webPlugin.devmode.off')}
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** As partes de um plugin web na lista de plugins da mesa. */
export function WebPluginExtras({ plugin, isGm }: { plugin: RoomPlugin; isGm: boolean }) {
  const t = useT();
  const [open, setOpen] = useState<'hooks' | 'dev' | null>(null);
  const hooks = plugin.web?.webhooks.length ?? 0;
  return (
    <div className="rd-webset">
      {plugin.web?.description ? <p className="rd-webset__muted">{plugin.web.description}</p> : null}
      <div className="rd-webset__row">
        {isGm && hooks ? (
          <Button variant="secondary" aria-expanded={open === 'hooks'} onClick={() => setOpen(open === 'hooks' ? null : 'hooks')}>
            {t('webPlugin.hooks', { count: hooks })}
          </Button>
        ) : null}
        <Button variant="secondary" aria-expanded={open === 'dev'} onClick={() => setOpen(open === 'dev' ? null : 'dev')}>
          {t('webPlugin.devmode')}
        </Button>
      </div>
      {open === 'hooks' ? <Integrations plugin={plugin} /> : null}
      {open === 'dev' ? <DevMode plugin={plugin} /> : null}
    </div>
  );
}
